import React from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Row,
  Table,
} from "reactstrap";
import QrCode from "qrcode.react";

import { dateFormatter } from "../../util/formatter";
import LoadingOverlay from "./LoadingOverlay";
import LoadingButtonIcon from "@/views/Components/LoadingButtonIcon";
import PropTypes from "prop-types";

const titles = {
  currencyName: "Currency Name",
  currencySymbol: "Currency Symbol",
  description: "Description",
  destinationAccountId: "Destination Account ID",
  destinationBankName: "Destination Bank Name",
  destinationBankParticipantCode: "Destination Bank Participant Code",
  destinationName: "Destination Full name",
  destinationPhoneNumber: "Destination Primary Contact",
  errorMessage: "Error Message",
  fastTransactionId: "Fast Transaction ID",
  fiTransactionId: "FI Transaction ID",
  precision: "Precision",
  sourceAccountId: "Source Account ID",
  sourceBankName: "Source Bank Name",
  sourceBankParticipantCode: "Source Bank Participant Code",
  sourceName: "Source Full name",
  sourcePhoneNumber: "Source Primary Contact",
  status: "Status",
  transactionCreatedTime: "Creation Time",
  trxDaytime: "Transaction Daytime",
  transactionHash: "Hash of Transaction",
  transactionWalletType: "Transaction Wallet Type",
  sourceWalletType: "Source Wallet Type",
  destinationWalletType: "Destination Wallet Type",
  assetId: "Asset ID",
  receiverBankAccount: "Receiver Bank Account",
  transactionType: "Transaction Type",
  fstTrxStatus: "Fast Transaction Status",
  qrCode: "QR Code",
  extRef: "External reference",
  pairedTrxHash: "Hash of Paired Transaction",
};

const exhcangeFields = {
  sourceCurrency: "Source Currency",
  targetCurrency: "Target Currency",
  exchangeRate: "Exchange Rate",
  exchangeRateBase: "Exchange Rate Base",
  sourceAmount: "Source Amount",
  targetAmount: "Target Amount",
  quotationDate: "Quotation Date",
  minSourceValue: "Min Source Value",
  minTargetAmount: "Min Target Amount",
};

const ASSETS = {
  "khr#nbc": "KHR",
  "usd#nbc": "USD",
};

const TransactionDetails = ({
  loading,
  details,
  onBack,
  type,
  isRefunding,
  isAcknowledging,
  onClickRefund,
  onClickAcknowledge,
}) => {
  const {
    destinationWalletType,
    transactionType,
    fstTrxStatus,
    sourceWalletType,
  } = details;
  const beautifulDetails = Object.keys(details)
    .map(x => ({
      title: titles[x] || x,
      key: x,
      value:
        x === "transactionCreatedTime" ? dateFormatter(details[x]) : details[x],
    }))
    .filter(
      detail =>
        !(
          detail.title === titles.destinationName &&
          destinationWalletType !== "MOBILE"
        ) &&
        !(detail.title === titles.sourceName && sourceWalletType !== "MOBILE"),
    );

  const shouldRenderDepositActionsButtons =
    type === "incoming" &&
    destinationWalletType === "BANK" &&
    transactionType === "Deposit" &&
    fstTrxStatus === "RECEIVED_AT_FI";

  const depositActionsButtons = (
    <>
      <Button
        type="submit"
        color="primary"
        onClick={onClickRefund}
        disabled={isAcknowledging}
        style={{ marginLeft: 20, width: 200 }}>
        <i className="fa fa-reply mr-2" />
        <span>Refund</span>
        <LoadingButtonIcon loading={isRefunding} />
      </Button>
      <Button
        type="submit"
        color="success"
        onClick={onClickAcknowledge}
        disabled={isRefunding}
        style={{ marginLeft: 20, width: 200 }}>
        <i className="fa fa-check-circle mr-2" />
        <span>Acknowledge</span>
        <LoadingButtonIcon loading={isAcknowledging} />
      </Button>
    </>
  );

  return (
    <div>
      <Row>
        <Col>
          <Card style={{ position: "relative" }}>
            <LoadingOverlay loading={loading} />
            <CardHeader>Transactions Detail</CardHeader>
            <CardBody>
              <Row>
                <Col
                  sm={{
                    size: 12,
                  }}
                  lg={{
                    size: 11,
                  }}
                  xl={{
                    size: 9,
                  }}>
                  <Table hover bordered striped responsive>
                    <tbody>
                      {beautifulDetails.length ? (
                        beautifulDetails
                          .filter(
                            detail => detail.value && detail.key !== "qrDetail",
                          )
                          .map(detail => {
                            if (detail.key === "amount") {
                              return null;
                            }

                            if (detail.key === "currencyExchangeRate") {
                              return Object.entries(exhcangeFields)
                                .filter(([key, value]) => detail.value[key])
                                .map(([key, value]) => (
                                  <tr key={key}>
                                    <td>{value}</td>
                                    <td>
                                      {key === "quotationDate"
                                        ? dateFormatter(detail.value[key], {
                                            dateFormat: "array",
                                            showTime: false,
                                          })
                                        : detail.value[key]}
                                    </td>
                                  </tr>
                                ));
                            }

                            if (detail.key === "qrCode") {
                              return (
                                <tr key={detail.key}>
                                  <td>{detail.title}</td>
                                  <td>
                                    <QrCode
                                      id="qrCode"
                                      size={256}
                                      value={detail.value}
                                    />
                                  </td>
                                </tr>
                              );
                            }

                            if (detail.key === "assetId") {
                              return (
                                <tr key={detail.key}>
                                  <td>{detail.title}</td>
                                  <td>{ASSETS[detail.value]}</td>
                                </tr>
                              );
                            }

                            if (detail.key === "transactionContent") {
                              const result = [];
                              const command = detail.value.commandList.find(
                                c => c.commandCase === "TRANSFER_ASSET",
                              );

                              if (command && command.amount) {
                                result.push(
                                  <>
                                    <tr key={`amount`}>
                                      <td>Amount</td>
                                      <td>{command.amount}</td>
                                    </tr>
                                  </>,
                                );
                              }

                              if (detail.value.details.sourceOfFunds) {
                                result.push(
                                  <>
                                    <tr key={`${detail.key}Name`}>
                                      <td>Transfer from (name)</td>
                                      <td>
                                        {
                                          detail.value.details.sourceOfFunds
                                            .name
                                        }
                                      </td>
                                    </tr>
                                    <tr key={`${detail.key}Phone`}>
                                      <td>Transfer from (phone)</td>
                                      <td>
                                        {
                                          detail.value.details.sourceOfFunds
                                            .phoneNumber
                                        }
                                      </td>
                                    </tr>
                                  </>,
                                );
                              }

                              return result;
                            }

                            return (
                              <tr key={detail.key}>
                                <td>{detail.title}</td>
                                <td>{detail.value}</td>
                              </tr>
                            );
                          })
                      ) : (
                        <tr>
                          <td>
                            {loading ? "Loading..." : "No transaction info"}
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </Table>
                </Col>
              </Row>
              <Row>
                <Col>
                  <Button
                    style={{ width: 100, marginLeft: 15 }}
                    color="primary"
                    type="button"
                    onClick={onBack}
                    data-cy="back">
                    <i className="fa fa-arrow-left mr-2" />
                    Back
                  </Button>
                  {shouldRenderDepositActionsButtons && depositActionsButtons}
                </Col>
              </Row>
            </CardBody>
          </Card>
        </Col>
      </Row>
    </div>
  );
};

TransactionDetails.propTypes = {
  loading: PropTypes.bool.isRequired,
  details: PropTypes.object.isRequired,
  onBack: PropTypes.func.isRequired,
  type: PropTypes.oneOf(["incoming", "outgoing", "history"]).isRequired,
  isRefunding: PropTypes.bool.isRequired,
  isAcknowledging: PropTypes.bool.isRequired,
  onClickRefund: PropTypes.func.isRequired,
  onClickAcknowledge: PropTypes.func.isRequired,
};

export default TransactionDetails;
